import { useEffect, useState } from "react";
import axios from "axios";
import {
  Users,
  KeyRound,
  ShieldOff,
  ShieldCheck,
  Database,
  RefreshCw,
  LogOut,
  Activity,
  ArrowUpRight,
  Banknote,
  Download,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/lib/auth";
import { API } from "@/lib/site";
import ExternalKeysPanel from "./ExternalKeysPanel";
import DeletionRequestsPanel from "./DeletionRequestsPanel";
import ChatInboxPanel from "./ChatInboxPanel";

const RANGES = [
  { value: 7, label: "7d" },
  { value: 14, label: "14d" },
  { value: 30, label: "30d" },
  { value: 90, label: "90d" },
];

function cents(v) {
  return (v / 100).toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function Sparkline({ data, color = "#0A1128", height = 50 }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data, 1);
  const w = 100;
  const step = w / Math.max(1, data.length - 1);
  const points = data
    .map((v, i) => `${i * step},${height - (v / max) * (height - 6) - 3}`)
    .join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${height}`} className={`w-full h-[${height}px]`} preserveAspectRatio="none">
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function Stat({ icon: Icon, label, value, sub, color = "#0A1128", testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-slate-200 p-5 bg-white af-shadow-card">
      <div className="flex items-center justify-between">
        <span className="text-[12px] uppercase tracking-[0.18em] text-slate-500">{label}</span>
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-slate-50" style={{ color }}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <p className="font-display text-[26px] mt-2 text-[#0A1128]">{value}</p>
      {sub && <p className="text-[12px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function Badge({ status }) {
  const map = {
    active: "bg-emerald-50 text-emerald-700",
    suspended: "bg-rose-50 text-rose-700",
  };
  return (
    <span className={`inline-flex items-center text-[11px] uppercase tracking-wider font-semibold rounded px-2 py-0.5 ${map[status] || "bg-slate-100 text-slate-600"}`}>
      {status}
    </span>
  );
}

export default function AdminDashboard() {
  const { profile, headers, logout } = useAuth();
  const [partners, setPartners] = useState([]);
  const [usage, setUsage] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const fetchPartners = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/partners`, { headers: headers() });
      setPartners(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const fetchUsage = async (d = days) => {
    try {
      const { data } = await axios.get(`${API}/admin/usage?days=${d}`, { headers: headers() });
      setUsage(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const fetchAll = async (d = days) => {
    setLoading(true);
    try {
      await Promise.all([fetchPartners(), fetchUsage(d)]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const setBusyFor = (id, key, val) =>
    setBusy((b) => ({ ...b, [`${id}:${key}`]: val }));

  const downloadCsv = async (format, partnerId = null) => {
    try {
      const params = new URLSearchParams({ days: String(days), format });
      if (partnerId) params.append("partner_id", partnerId);
      const res = await axios.get(`${API}/admin/usage/export?${params}`, {
        headers: headers(),
        responseType: "blob",
      });
      const disposition = res.headers["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/i.exec(disposition);
      const filename = match ? match[1] : `atlasforge-${format}-${days}d.csv`;
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Downloaded.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const clearKey = async (p, env) => {
    if (!window.confirm(`Clear the ${env.toUpperCase()} API key for ${p.company_name}? The partner will need to re-paste the new key from their email.`)) return;
    setBusyFor(p.id, `regen-${env}`, true);
    try {
      await axios.post(`${API}/admin/partners/${p.id}/clear-key?environment=${env}`, {}, { headers: headers() });
      toast.success(`${env.toUpperCase()} key cleared for ${p.company_name}.`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyFor(p.id, `regen-${env}`, false);
    }
  };

  const seed = async (p) => {
    setBusyFor(p.id, "seed", true);
    try {
      const { data } = await axios.post(`${API}/admin/partners/${p.id}/seed-demo`, {}, { headers: headers() });
      toast.success(`Seeded ${data.inserted_events.toLocaleString()} events for ${p.company_name}`);
      fetchAll(days);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyFor(p.id, "seed", false);
    }
  };

  const toggleSuspend = async (p) => {
    const action = p.status === "active" ? "suspend" : "activate";
    setBusyFor(p.id, "status", true);
    try {
      await axios.post(`${API}/admin/partners/${p.id}/${action}`, {}, { headers: headers() });
      toast.success(`${p.company_name} ${action}d`);
      fetchPartners();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyFor(p.id, "status", false);
    }
  };

  const deletePartner = async (p) => {
    const phrase = `delete ${p.company_name}`;
    const answer = window.prompt(
      `This will PERMANENTLY delete ${p.company_name} along with all their events and API keys.\n\nTo confirm, type:\n${phrase}`
    );
    if (answer !== phrase) {
      if (answer !== null) toast.error("Confirmation text didn't match. Deletion cancelled.");
      return;
    }
    setBusyFor(p.id, "delete", true);
    try {
      const { data } = await axios.delete(`${API}/admin/partners/${p.id}`, { headers: headers() });
      toast.success(
        `Deleted ${p.company_name} (${(data.deleted_events || 0).toLocaleString()} events removed)`
      );
      fetchAll(days);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyFor(p.id, "delete", false);
    }
  };

  const summary = usage?.summary || { active_users: 0, new_signups: 0, accounts_linked: 0, transactions: 0, txn_volume_cents: 0 };
  const series = usage?.series || [];
  const perPartner = usage?.per_partner || [];
  const topCategories = usage?.top_categories || [];
  const seriesUsers = series.map((s) => s.active_users);
  const seriesTxn = series.map((s) => s.transactions);
  const seriesVol = series.map((s) => s.txn_volume_cents);

  // Merge partner profile data with usage rows by id for the partner table
  const partnersWithUsage = partners.map((p) => {
    const u = perPartner.find((x) => x.partner_id === p.id) || {
      active_users: 0, new_signups: 0, accounts_linked: 0, transactions: 0, txn_volume_cents: 0,
    };
    return { ...p, usage: u };
  });

  return (
    <section className="bg-[#F7F9FC] min-h-[80vh]">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-10">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">Admin Console</p>
            <h1 data-testid="admin-dashboard-title" className="font-display text-[28px] md:text-[34px] text-[#0A1128]">
              Platform-wide usage
            </h1>
            <p className="text-[13px] text-slate-500 mt-1">Signed in as {profile?.email}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-full bg-white border border-slate-200 p-1">
              {RANGES.map((r) => (
                <button
                  key={r.value}
                  onClick={() => setDays(r.value)}
                  data-testid={`admin-range-${r.value}`}
                  className={`text-[12px] font-medium px-3 py-1.5 rounded-full transition ${
                    days === r.value ? "bg-[#0A1128] text-white" : "text-slate-600 hover:text-[#0A1128]"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => fetchAll(days)}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white text-[#0A1128] text-[13px] font-medium px-4 py-2 hover:border-slate-400"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              onClick={() => downloadCsv("partners")}
              data-testid="admin-download-partners"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white text-[#0A1128] text-[13px] font-medium px-4 py-2 hover:border-slate-400"
              title="Per-partner totals for this range"
            >
              <Download className="h-3.5 w-3.5" /> Partners CSV
            </button>
            <button
              onClick={() => downloadCsv("summary")}
              data-testid="admin-download-summary"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white text-[#0A1128] text-[13px] font-medium px-4 py-2 hover:border-slate-400"
              title="Daily platform-wide summary"
            >
              <Download className="h-3.5 w-3.5" /> Summary CSV
            </button>
            <button
              onClick={() => downloadCsv("events")}
              data-testid="admin-download-events"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white text-[#0A1128] text-[13px] font-medium px-4 py-2 hover:border-slate-400"
              title="Full event-level export (all partners)"
            >
              <Download className="h-3.5 w-3.5" /> All events CSV
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 text-slate-600 text-[13px] font-medium px-4 py-2 hover:bg-slate-100"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>

        {/* Platform KPIs */}
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <Stat testId="admin-stat-active-users" icon={Users} label="Active users (all partners)" value={summary.active_users.toLocaleString()} sub={`${summary.new_signups.toLocaleString()} new signups`} color="#2563EB" />
          <Stat testId="admin-stat-accounts" icon={Banknote} label="Accounts linked" value={summary.accounts_linked.toLocaleString()} sub={`Last ${days} days`} color="#00A373" />
          <Stat testId="admin-stat-transactions" icon={Activity} label="Transactions" value={summary.transactions.toLocaleString()} sub={`Last ${days} days`} color="#0A1128" />
          <Stat testId="admin-stat-volume" icon={ArrowUpRight} label="Transaction volume" value={cents(summary.txn_volume_cents)} sub={`Last ${days} days`} color="#FF5A36" />
        </div>

        {/* Trend cards */}
        <div className="mt-6 grid lg:grid-cols-3 gap-5">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 af-shadow-card">
            <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">Active users</p>
            <p className="font-display text-[22px] mt-1 text-[#0A1128]">{summary.active_users.toLocaleString()}</p>
            <Sparkline data={seriesUsers} color="#2563EB" />
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 af-shadow-card">
            <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">Transactions / day</p>
            <p className="font-display text-[22px] mt-1 text-[#0A1128]">{Math.round(summary.transactions / Math.max(days, 1)).toLocaleString()}</p>
            <Sparkline data={seriesTxn} color="#0A1128" />
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 af-shadow-card">
            <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">Volume / day</p>
            <p className="font-display text-[22px] mt-1 text-[#0A1128]">{cents(Math.round(summary.txn_volume_cents / Math.max(days, 1)))}</p>
            <Sparkline data={seriesVol} color="#FF5A36" />
          </div>
        </div>

        {/* Top categories */}
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 af-shadow-card">
          <p className="font-display text-[18px]">Top transaction categories (all partners)</p>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {topCategories.map((c) => {
              const max = Math.max(...topCategories.map((x) => x.volume_cents), 1);
              const pct = (c.volume_cents / max) * 100;
              return (
                <div key={c.category} data-testid={`admin-category-${c.category}`}>
                  <div className="flex justify-between text-[13px] text-slate-700">
                    <span className="capitalize">{c.category}</span>
                    <span className="font-medium">{cents(c.volume_cents)}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 mt-1 overflow-hidden">
                    <div className="h-full bg-[#0A1128]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
            {topCategories.length === 0 && (
              <p className="text-[13px] text-slate-500">No transactions in this range.</p>
            )}
          </div>
        </div>

        {/* Per-partner usage table */}
        <div className="mt-8">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-[22px] text-[#0A1128]">Per-partner breakdown</h2>
            <p className="text-[12px] text-slate-500">Sorted by transaction volume</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white af-shadow-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-[13.5px]">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr>
                    <th className="font-medium px-4 py-3">Partner</th>
                    <th className="font-medium px-4 py-3">Status</th>
                    <th className="font-medium px-4 py-3 text-right">Active users</th>
                    <th className="font-medium px-4 py-3 text-right">New</th>
                    <th className="font-medium px-4 py-3 text-right">Linked</th>
                    <th className="font-medium px-4 py-3 text-right">Txns</th>
                    <th className="font-medium px-4 py-3 text-right">Volume</th>
                    <th className="font-medium px-4 py-3 text-right">Events</th>
                    <th className="font-medium px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {partnersWithUsage
                    .slice()
                    .sort((a, b) => (b.usage.txn_volume_cents || 0) - (a.usage.txn_volume_cents || 0))
                    .map((p) => (
                      <tr key={p.id} data-testid={`admin-partner-${p.id}`} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-[#0A1128]">{p.company_name}</p>
                          <p className="text-[12px] text-slate-500">{p.email}</p>
                        </td>
                        <td className="px-4 py-3"><Badge status={p.status} /></td>
                        <td className="px-4 py-3 text-right">{p.usage.active_users.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right">{p.usage.new_signups.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right">{p.usage.accounts_linked.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right">{p.usage.transactions.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right font-medium text-[#0A1128]">{cents(p.usage.txn_volume_cents)}</td>
                        <td className="px-4 py-3 text-right text-slate-500">{(p.events_count || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                            <button
                              onClick={() => downloadCsv("events", p.id)}
                              data-testid={`admin-download-partner-${p.id}`}
                              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white text-[11px] font-medium px-2 py-1 hover:border-slate-400"
                              title="Download this partner's events"
                            >
                              <Download className="h-3 w-3" /> CSV
                            </button>
                            <button
                              onClick={() => clearKey(p, "live")}
                              disabled={busy[`${p.id}:regen-live`]}
                              data-testid={`admin-regen-live-${p.id}`}
                              className="inline-flex items-center gap-1 rounded-md bg-[#0A1128] text-white text-[11px] font-medium px-2 py-1 hover:bg-[#1E293B] disabled:opacity-50"
                            >
                              <KeyRound className="h-3 w-3" /> Clear live
                            </button>
                            <button
                              onClick={() => clearKey(p, "test")}
                              disabled={busy[`${p.id}:regen-test`]}
                              data-testid={`admin-regen-test-${p.id}`}
                              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white text-[11px] font-medium px-2 py-1 hover:border-slate-400 disabled:opacity-50"
                            >
                              <KeyRound className="h-3 w-3" /> Clear test
                            </button>
                            <button
                              onClick={() => seed(p)}
                              disabled={busy[`${p.id}:seed`]}
                              data-testid={`admin-seed-${p.id}`}
                              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white text-[11px] font-medium px-2 py-1 hover:border-slate-400 disabled:opacity-50"
                            >
                              <Database className="h-3 w-3" /> Seed
                            </button>
                            <button
                              onClick={() => toggleSuspend(p)}
                              disabled={busy[`${p.id}:status`]}
                              data-testid={`admin-toggle-${p.id}`}
                              className={`inline-flex items-center gap-1 rounded-md text-[11px] font-medium px-2 py-1 disabled:opacity-50 ${
                                p.status === "active"
                                  ? "border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100"
                                  : "border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                              }`}
                            >
                              {p.status === "active" ? (
                                <>
                                  <ShieldOff className="h-3 w-3" /> Suspend
                                </>
                              ) : (
                                <>
                                  <ShieldCheck className="h-3 w-3" /> Activate
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => deletePartner(p)}
                              disabled={busy[`${p.id}:delete`]}
                              data-testid={`admin-delete-${p.id}`}
                              className="inline-flex items-center gap-1 rounded-md border border-rose-300 bg-white text-rose-700 text-[11px] font-medium px-2 py-1 hover:bg-rose-50 disabled:opacity-50"
                              title="Permanently delete this partner and all their data"
                            >
                              <Trash2 className="h-3 w-3" /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  {!loading && partnersWithUsage.length === 0 && (
                    <tr>
                      <td colSpan={9} className="p-6 text-center text-slate-500 text-[14px]">No partners yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <ChatInboxPanel />
        <ExternalKeysPanel />
        <DeletionRequestsPanel />
      </div>
    </section>
  );
}
